import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Badge, Spinner, Alert, Modal, Form, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetSyncHistory, useGetUsersId, getSyncHistorySyncRunId, deleteSyncHistorySyncRunId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { SyncRun, SyncRunItem } from '../api/generated/model';
import { GetSyncHistorySyncRunIdParamsOperation } from '../api/generated/model';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { RootState } from '../store';

const statusBadge = (run: SyncRun) => {
  const colors: Record<string, string> = { completed: 'success', running: 'primary', failed: 'danger', error: 'danger' };
  return <Badge bg={colors[run.status || ''] || 'secondary'}>{run.status}</Badge>;
};

const triggerBadge = (run: SyncRun) => {
  const colors: Record<string, string> = { periodic: 'info', manual: 'warning', webhook: 'secondary', settings_change: 'dark' };
  return <Badge bg={colors[run.trigger_type || ''] || 'secondary'}>{run.trigger_type}</Badge>;
};

const formatDuration = (run: SyncRun) => {
  if (run.duration) return run.duration;
  return '-';
};

const formatDateTime = (dt?: string | null) => {
  if (!dt) return '-';
  return new Date(dt).toISOString();
};

const SyncHistoryPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  const [triggerFilter, setTriggerFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortField, setSortField] = useState('started_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [detailSortField, setDetailSortField] = useState('created_at');
  const [detailSortDir, setDetailSortDir] = useState<'asc' | 'desc'>('asc');
  const [detailTypeFilter, setDetailTypeFilter] = useState('');
  const [detailOpFilter, setDetailOpFilter] = useState('');
  const [detailSearch, setDetailSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(detailSearch), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [detailSearch]);
  const [detailRun, setDetailRun] = useState<SyncRun | null>(null);
  const [detailSummary, setDetailSummary] = useState<any | null>(null);
  const [detailEntityTypes, setDetailEntityTypes] = useState<string[]>([]);
  const [detailItems, setDetailItems] = useState<SyncRunItem[]>([]);
  const [detailHasMore, setDetailHasMore] = useState(false);
  const [detailCursor, setDetailCursor] = useState<string | undefined>(undefined);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadingMore, setDetailLoadingMore] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadDetail = useCallback(async (runId: string, cursor?: string, opts?: { sort_field?: string; sort_direction?: string; entity_type?: string; operation?: string; search?: string }) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) {
      setDetailLoadingMore(true);
    } else {
      setDetailLoading(true);
      setDetailItems([]);
    }
    try {
      let params: any;
      if (cursor) {
        // Subsequent pages: cursor contains all state
        params = { cursor };
      } else {
        // First page: send filters
        params = { limit: 50, sort_field: opts?.sort_field || 'created_at', sort_direction: opts?.sort_direction || 'desc' };
        // Always include sort_field and sort_direction (required for cursor-based pagination)
        if (opts?.entity_type) params.entity_type = opts.entity_type;
        if (opts?.operation) params.operation = opts.operation;
        if (opts?.search) params.search = opts.search;
      }
      const result = await getSyncHistorySyncRunId(runId, params);
      setDetailError(null);
      if (result.sync_run) setDetailRun(result.sync_run);
      if ((result as any).summary) setDetailSummary((result as any).summary);
      if (result.entity_types) setDetailEntityTypes(result.entity_types);
      const newItems = result.items || [];
      setDetailItems(prev => isLoadMore ? [...prev, ...newItems] : newItems);
      setDetailHasMore(!!result.pagination?.cursor);
      setDetailCursor(result.pagination?.cursor || undefined);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setDetailError(msg);
      setDetailHasMore(false);
      setDetailCursor(undefined);
    } finally {
      setDetailLoading(false);
      setDetailLoadingMore(false);
    }
  }, []);

  const reloadDetail = useCallback(() => {
    if (!selectedRunId) return;
    const opts: any = {};
    if (detailSortField) opts.sort_field = detailSortField;
    if (detailSortDir) opts.sort_direction = detailSortDir;
    if (detailTypeFilter) opts.entity_type = detailTypeFilter;
    if (detailOpFilter) opts.operation = detailOpFilter;
    if (debouncedSearch) opts.search = debouncedSearch;
    loadDetail(selectedRunId, undefined, opts);
  }, [selectedRunId, detailSortField, detailSortDir, detailTypeFilter, detailOpFilter, debouncedSearch, loadDetail]);

  useEffect(() => {
    if (selectedRunId) {
      reloadDetail();
    } else {
      setDetailRun(null);
      setDetailItems([]);
      setDetailHasMore(false);
      setDetailCursor(undefined);
    }
  }, [selectedRunId, reloadDetail]);

  const handleDeleteRun = useCallback(async (runId: string) => {
    if (!window.confirm('Delete this sync run and all its items?')) return;
    try {
      await deleteSyncHistorySyncRunId(runId);
      if (selectedRunId === runId) {
        setSelectedRunId(null);
      }
      // Force refetch by incrementing a key — useGetSyncHistory will refetch on next render
      window.location.reload();
    } catch (err: any) {
      console.error('Failed to delete sync run:', err);
      alert('Failed to delete sync run: ' + (err?.message || 'Unknown error'));
    }
  }, [selectedRunId]);

  const params: any = { limit: 50, sort_field: sortField, sort_direction: sortDir };
  if (triggerFilter) params.trigger_type = triggerFilter;
  if (statusFilter) params.status = statusFilter;

  const { data, isLoading, error: listError } = useGetSyncHistory(params);

  const runs: SyncRun[] = data?.data || [];

  const columns: ColumnConfig<SyncRun>[] = [
    {
      key: 'started_at', label: 'Time', width: 180, sortable: true,
      sortValue: (run) => run.started_at || '',
      renderCell: (run) => formatDateTime(run.started_at),
    },
    {
      key: 'integration_type', label: 'Source', width: 100, sortable: true,
      renderCell: (run) => {
        const labels: Record<string, string> = { quickbooks: 'QuickBooks', mercury: 'Mercury', events: 'Events', sling: 'Sling', harvestmgr: 'Applicant Harvester' };
        return <Badge bg="outline-secondary" className="border text-body">{labels[run.integration_type || ''] || run.integration_type || '-'}</Badge>;
      },
    },
    {
      key: 'integration_name', label: 'Integration', width: 150, sortable: true,
      renderCell: (run) => <span className="text-muted small">{run.integration_name || '-'}</span>,
    },
    {
      key: 'trigger_type', label: 'Trigger', width: 100, sortable: true,
      renderCell: triggerBadge,
    },
    {
      key: 'status', label: 'Status', width: 100, sortable: true,
      renderCell: statusBadge,
    },
    {
      key: 'duration_ms', label: 'Duration', width: 100, sortable: true,
      renderCell: (run) => formatDuration(run),
    },
    {
      key: 'changes', label: 'Changes', width: 200,
      renderCell: (run) => {
        const c = run.created || 0;
        const u = run.updated || 0;
        const d = run.deleted || 0;
        const e = run.errors || 0;
        if (c + u + d + e === 0) return <span className="text-muted">No changes</span>;
        return (
          <span>
            {c > 0 && <Badge bg="success" className="me-1">{c} created</Badge>}
            {u > 0 && <Badge bg="info" className="me-1">{u} updated</Badge>}
            {d > 0 && <Badge bg="danger" className="me-1">{d} deleted</Badge>}
            {e > 0 && <Badge bg="warning" className="me-1">{e} errors</Badge>}
          </span>
        );
      },
    },
    {
      key: 'error_message', label: 'Error', width: 200,
      renderCell: (run) => run.error_message ? <span className="text-danger small">{run.error_message}</span> : null,
    },
  ];

  const opColors: Record<string, string> = { create: 'success', update: 'info', delete: 'danger', error: 'warning', warning: 'warning' };

  const detailColumns: ColumnConfig<SyncRunItem>[] = [
    {
      key: 'operation', label: 'Operation', width: 90, sortable: true,
      renderCell: (item) => <Badge bg={opColors[item.operation || ''] || 'secondary'}>{item.operation}</Badge>,
    },
    { key: 'entity_type', label: 'Type', width: 130, sortable: true },
    { key: 'entity_id', label: 'ID', width: 80, sortable: true },
    {
      key: 'summary', label: 'Summary', width: 300, minWidth: 300, sortable: true,
      sortValue: (item) => item.summary || '',
      renderCell: (item) => <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.summary}</span>,
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout title="Sync History">
        <div className="text-center py-5">
          <Spinner animation="border" /> Loading sync history...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Sync History" subtitle="Review QuickBooks sync activity from webhooks, manual syncs, and periodic syncs" scrollable>
      {listError && (
        <Alert variant="danger" className="mb-3">{(listError as any)?.response?.data?.message || (listError as any)?.message || 'Failed to load sync history'}</Alert>
      )}
      {/* Filters */}
      <div className="d-flex gap-3 mb-3">
        <Form.Select
          size="sm"
          style={{ width: '150px' }}
          value={triggerFilter}
          onChange={(e) => setTriggerFilter(e.target.value)}
        >
          <option value="">All Triggers</option>
          <option value="periodic">Periodic</option>
          <option value="manual">Manual</option>
          <option value="webhook">Webhook</option>
          <option value="settings_change">Settings Change</option>
        </Form.Select>
        <Form.Select
          size="sm"
          style={{ width: '150px' }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="running">Running</option>
          <option value="failed">Failed</option>
          <option value="error">Error</option>
        </Form.Select>
      </div>

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <span className="fw-semibold">Sync Runs</span>
          <span className="ms-2 opacity-75">({runs.length})</span>
        </Card.Header>
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={runs}
            columns={columns}
            getRowKey={(run) => run.id || ''}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
            onRowClick={(run) => setSelectedRunId(run.id || null)}
            preferencesKey="sync_history"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableStickyHeader={true}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            emptyState={
              <Alert variant="info" className="m-3">
                No sync runs found. Sync runs will appear here after a QuickBooks sync is triggered.
              </Alert>
            }
          />
        </Card.Body>
      </Card>

      {/* Detail Modal */}
      <Modal show={!!selectedRunId} onHide={() => {
        setSelectedRunId(null);
        setDetailTypeFilter('');
        setDetailOpFilter('');
        setDetailSearch('');
        setDebouncedSearch('');
        setDetailSortField('created_at');
        setDetailSortDir('asc');
        setDetailEntityTypes([]);
        setDetailError(null);
      }} size="xl" scrollable dialogClassName="sync-detail-modal">
        <style>{`
          .sync-detail-modal .modal-content { height: calc(100vh - 3.5rem); }
          .sync-detail-modal .modal-body { display: flex; flex-direction: column; }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 flex-grow-1">
            Sync Run Details
            {detailRun && triggerBadge(detailRun)}
            {detailRun && statusBadge(detailRun)}
            {selectedRunId && (
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-auto me-2"
                onClick={() => handleDeleteRun(selectedRunId)}
              >
                <i className="bi bi-trash me-1"></i>Delete
              </Button>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailError && (
            <Alert variant="danger" className="mb-3">{detailError}</Alert>
          )}
          {detailLoading ? (
            <div className="text-center py-3"><Spinner animation="border" /></div>
          ) : detailRun ? (() => {
            const run = detailRun;
            const s = detailSummary || {};
            const c = s.creates || 0;
            const u = s.updates || 0;
            const d = s.deletes || 0;
            const dl = s.downloads || 0;
            const sm = s.summaries || 0;
            const e = s.errors || 0;
            const hasChanges = c + u + d + dl + sm + e > 0;

            return (
              <>
                {/* Summary row */}
                <div className="d-flex gap-4 text-muted small mb-3">
                  <div><i className="bi bi-play-circle me-1"></i>{formatDateTime(run.started_at)}</div>
                  <div><i className="bi bi-stop-circle me-1"></i>{formatDateTime(run.completed_at)}</div>
                  <div><i className="bi bi-stopwatch me-1"></i>{formatDuration(run)}</div>
                </div>

                {/* Stats */}
                {hasChanges && (
                  <div className="d-flex gap-3 mb-3">
                    {c > 0 && <div className="text-center px-3 py-2 bg-body-tertiary rounded"><div className="fs-4 fw-bold text-success">{c}</div><div className="text-muted small">Created</div></div>}
                    {u > 0 && <div className="text-center px-3 py-2 bg-body-tertiary rounded"><div className="fs-4 fw-bold text-info">{u}</div><div className="text-muted small">Updated</div></div>}
                    {d > 0 && <div className="text-center px-3 py-2 bg-body-tertiary rounded"><div className="fs-4 fw-bold text-danger">{d}</div><div className="text-muted small">Deleted</div></div>}
                    {dl > 0 && <div className="text-center px-3 py-2 bg-body-tertiary rounded"><div className="fs-4 fw-bold text-primary">{dl}</div><div className="text-muted small">Downloads</div></div>}
                    {sm > 0 && <div className="text-center px-3 py-2 bg-body-tertiary rounded"><div className="fs-4 fw-bold text-secondary">{sm}</div><div className="text-muted small">Summaries</div></div>}
                    {e > 0 && <div className="text-center px-3 py-2 bg-body-tertiary rounded"><div className="fs-4 fw-bold text-warning">{e}</div><div className="text-muted small">Errors</div></div>}
                  </div>
                )}

                {/* Error message */}
                {run.error_message && (
                  <Alert variant="danger" className="mb-3">{run.error_message}</Alert>
                )}

                {/* Items */}
                <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
                  <Card.Header className="bg-primary text-white flex-shrink-0">
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Select size="sm" style={{ width: '200px' }} value={detailTypeFilter} onChange={(e) => setDetailTypeFilter(e.target.value)}>
                        <option value="">All Types</option>
                        {detailEntityTypes.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </Form.Select>
                      <Form.Select size="sm" style={{ width: '120px' }} value={detailOpFilter} onChange={(e) => setDetailOpFilter(e.target.value)}>
                        <option value="">All Ops</option>
                        {Object.values(GetSyncHistorySyncRunIdParamsOperation).map(op => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Search ID or summary..."
                        value={detailSearch}
                        onChange={(e) => setDetailSearch(e.target.value)}
                        style={{ maxWidth: '250px' }}
                      />
                    </div>
                  </Card.Header>
                  <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
                    <DataTable
                      data={detailItems}
                      columns={detailColumns}
                      getRowKey={(item) => item.id || ''}
                      sortConfig={{ field: detailSortField, direction: detailSortDir }}
                      onSortChange={(field, dir) => { setDetailSortField(field); setDetailSortDir(dir); }}
                      preferencesKey="sync_history_detail"
                      userId={user?.id}
                      userPreferences={userData?.preferences}
                      maxHeight="100%"
                      isLoading={detailLoadingMore}
                      enableStickyHeader={true}
                      enableColumnReorder={true}
                      enableColumnResize={true}
                      enableColumnVisibility={true}
                      pagination={detailHasMore ? { cursor: detailCursor || '', hasMore: detailHasMore, limit: 50 } : undefined}
                      onLoadMore={detailHasMore && selectedRunId && detailCursor ? () => {
                        loadDetail(selectedRunId!, detailCursor!);
                      } : undefined}
                      emptyState={
                        <div className="text-center text-muted py-3">
                          <i className="bi bi-check-circle fs-3 d-block mb-2 text-success"></i>
                          No individual item changes recorded.
                        </div>
                      }
                    />
                  </Card.Body>
                </Card>
              </>
            );
          })() : null}
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  );
};

export default SyncHistoryPage;
